import React, { useCallback, useEffect, useState } from 'react'
import Wrapper from '../../components/Wrapper'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import { toast } from 'react-toastify'
import { greenTheme, inputStyles, inputLabelStyles, selectStyles, sectionTitleStyles, buttonStyles } from '../../helpers/style'
import * as XLSX from 'xlsx'
import { menu, weekDays, auto, short, input } from '../../translate'
import Api from '../../Api'
import { useDispatch, useSelector } from 'react-redux'
import { fileRequest } from '../../store/actions/file'

function normalizeHeader(h) {
  if (h == null) return ''
  return String(h).trim().toLowerCase().replace(/\s+/g, ' ')
}

/** Parse Excel to array of phone numbers (one column: Phone, Number, or first column) */
function parsePhoneNumbersFromExcel(rows, headerMap) {
  const phones = []
  const phoneCol =
    headerMap['phone'] ?? headerMap['number'] ?? headerMap['tel'] ?? headerMap['phone number'] ?? 0
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]
    if (!row || row.every((c) => c == null || String(c).trim() === '')) continue
    const val = row[phoneCol]
    const phone = val != null ? String(val).trim() : ''
    if (phone) phones.push(phone)
  }
  return phones
}

function AutoCallExcelUpload() {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const files = useSelector((state) => state.file.fileData)
  const isAddMode = id === 'add'

  const [form, setForm] = useState({
    type: 'auto',
    num: '',
    weekStart: weekDays[1][langIndex],
    weekEnd: weekDays[0][langIndex],
    timeStart: '10:00',
    timeEnd: '22:59',
    attempt: 2,
    okStart: 0,
    okEnd: 60,
  })
  const [inAuto, setInAuto] = useState([])
  const [inAuto1, setInAuto1] = useState([])
  const [processing, setProcessing] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => {
    if (isAddMode) {
      ;(async () => {
        try {
          const { data } = await Api.getInAuto()
          setInAuto(data.inputNumber?.buddies || [])
          setInAuto1(data.inputNumber?.shorts || [])
        } catch (e) {
          console.log(e)
        }
      })()
    }
  }, [isAddMode])

  useEffect(() => {
    if (!isAddMode && id) {
      ;(async () => {
        try {
          const { data } = await Api.singleAuto(id)
          setInAuto(data.memberGroups || [])
        } catch (e) {
          console.log(e)
        }
      })()
    }
  }, [isAddMode, id])

  useEffect(() => {
    if (form.type === 'group' && (!files || files.length === 0)) {
      dispatch(fileRequest(1))
    }
  }, [form.type, dispatch, files?.length])

  const handleChange = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }))
  }, [])

  const handleTimeInput = useCallback((name, value) => {
    if (/^([0-9]{0,2}):?([0-9]{0,2})?$/.test(value) || value === '') {
      setForm((prev) => ({ ...prev, [name]: value }))
    }
  }, [])

  const handleTimeBlur = useCallback((name) => {
    setForm((prev) => {
      const v = prev[name]
      if (!v) return prev
      const parts = v.split(':').map(Number)
      const hours = isNaN(parts[0]) ? 0 : Math.min(23, Math.max(0, parts[0]))
      const minutes = isNaN(parts[1]) ? 0 : Math.min(59, Math.max(0, parts[1]))
      return { ...prev, [name]: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` }
    })
  }, [])

  const handleFileChange = useCallback(
    (ev) => {
      const file = ev.target.files?.[0]
      if (file) {
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
        if (['.xlsx', '.xls', '.csv'].includes(ext)) {
          setSelectedFile(file)
        } else {
          toast.error(
            langIndex === 1
              ? 'Please select an Excel file (.xlsx, .xls, or .csv)'
              : langIndex === 2
                ? 'Выберите файл Excel (.xlsx, .xls или .csv)'
                : 'Խնդրում ենք ընտրել Excel ֆայլ'
          )
          ev.target.value = ''
        }
      }
    },
    [langIndex]
  )

  const handleDownloadTemplate = useCallback(() => {
    const data = [['Phone'], ['+1234567890'], ['+0987654321'], ['+5551234567']]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Phones')
    const fileName =
      langIndex === 1 ? 'phone_numbers.xlsx' : langIndex === 2 ? 'номера.xlsx' : 'hamary.xlsx'
    XLSX.writeFile(wb, fileName)
  }, [langIndex])

  const handleUpload = useCallback(() => {
    if (!form.num) {
      toast.error(
        langIndex === 1 ? 'Please select IVR/Short or File' : langIndex === 2 ? 'Выберите IVR/Short или файл' : 'Ընտրեք IVR/Short կամ ֆայլ'
      )
      return
    }
    if (!selectedFile) {
      toast.error(
        langIndex === 1 ? 'Please select a file first' : langIndex === 2 ? 'Сначала выберите файл' : 'Խնդրում ենք նախ ընտրել ֆայլ'
      )
      return
    }

    setProcessing(true)
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })
        if (!rows.length) {
          toast.error(langIndex === 1 ? 'File is empty' : langIndex === 2 ? 'Файл пуст' : 'Ֆայլը դատարկ է')
          setProcessing(false)
          return
        }
        const rawHeaders = rows[0]
        const headerMap = {}
        rawHeaders.forEach((h, i) => {
          const n = normalizeHeader(h)
          if (n) headerMap[n] = i
        })
        const phones = parsePhoneNumbersFromExcel(rows, headerMap)
        if (!phones.length) {
          toast.error(
            langIndex === 1
              ? 'No phone numbers found. Use a column named "Phone" or "Number".'
              : langIndex === 2
                ? 'Номера не найдены. Используйте столбец "Phone" или "Number".'
                : 'Հեռախոսահամարներ չեն գտնվել:'
          )
          setProcessing(false)
          return
        }
        const timeItems = phones.map((phone) => ({
          id: new Date().toISOString() + Math.random(),
          num: form.num,
          weekStart: form.weekStart,
          weekEnd: form.weekEnd,
          timeStart: form.timeStart,
          timeEnd: form.timeEnd,
          attempt: form.attempt,
          okStart: form.okStart,
          okEnd: form.okEnd,
          phone,
        }))
        navigate('/auto_call_single/add?campaignType=autocall', { state: { uploadedTime: timeItems } })
        toast.success(
          langIndex === 1
            ? `${timeItems.length} number(s) loaded with selected schedule`
            : langIndex === 2
              ? `Загружено ${timeItems.length} номеров`
              : `Բեռնվել է ${timeItems.length} համար`
        )
      } catch (err) {
        console.error(err)
        toast.error(
          langIndex === 1 ? 'Failed to read file.' : langIndex === 2 ? 'Ошибка чтения файла.' : 'Ֆայլը կարդալ չհաջողվեց:'
        )
      } finally {
        setProcessing(false)
      }
    }
    reader.readAsArrayBuffer(selectedFile)
  }, [form, selectedFile, navigate, langIndex])

  const location = useLocation()
  const handleBack = useCallback(() => {
    const fromList = location.state?.fromList
    const campaignType = location.state?.campaignType
    const query = new URLSearchParams()
    if (fromList) query.set('fromList', fromList)
    if (campaignType) query.set('campaignType', campaignType)
    const search = query.toString() ? `?${query.toString()}` : ''
    const targetPath = isAddMode ? `/auto_call_single/add${search}` : `/auto_call_single/${id}${search}`
    const hasHistory = window.history.length > 1
    if (hasHistory) {
      navigate("/auto_call_list")
    } else {
      navigate(targetPath, { state: location.state || undefined })
    }
  }, [navigate, isAddMode, id, location.state])

  const handleFileUploadEdit = useCallback(
    async (ev) => {
      const file = ev?.target?.files?.[0]
      if (!file || !id) return
      try {
        setProcessing(true)
        toast.info(
          langIndex === 1
            ? 'Uploading file, please wait while numbers are added…'
            : langIndex === 2
              ? 'Идёт загрузка файла, подождите пока номера будут добавлены…'
              : 'Ֆայլը վերբեռնվում է, սպասեք մինչև համարները ավելացվեն…'
        )
        await Api.autoCallUpload(file, id, form)
        toast.success(
          langIndex === 1
            ? 'File uploaded and numbers added'
            : langIndex === 2
              ? 'Файл загружен, номера добавлены'
              : 'Ֆայլը վերբեռնվեց, համարները ավելացվեցին'
        )
        navigate('/auto_call_list')
      } catch (e) {
        toast.error('Error happened ' + (e?.response?.data?.message || e?.message))
      } finally {
        setProcessing(false)
      }
    },
    [id, form, navigate, langIndex]
  )

  const numOptionsAuto = [
    ...(inAuto?.map((k) => ({ value: k.name, label: k.title || k.name })) || []),
    ...(inAuto1?.map((k) => ({ value: k.name, label: k.title || k.name })) || []),
  ]
  const numOptionsGroup = files?.map((f) => ({ value: f.fname, label: f.name || f.fname })) || []
  const numOptionsEdit = inAuto?.map((k) => ({ value: k.azang, label: `${k.azang} (${k.comment || ''})` })) || []

  const infoText = isAddMode
    ? (langIndex === 1
        ? 'Select the schedule (IVR/Short or File, days, time, attempt, OK range) below. Then upload an Excel file that contains only phone numbers (one per row in a column named "Phone" or "Number"). The same schedule will apply to all numbers.'
        : langIndex === 2
          ? 'Выберите расписание ниже, затем загрузите Excel только с номерами телефонов (один столбец "Phone" или "Number"). К всем номерам будет применено одно расписание.'
          : 'Ընտրեք ժամանակացույցը, ապա վերբեռնեք Excel ֆայլ միայն հեռախոսահամարներով: Բոլոր համարներին կկիրառվի նույն ժամանակացույցը:')
    : (langIndex === 1
        ? 'Select the schedule below and choose a file to upload. The file will be processed with the selected options.'
        : langIndex === 2
          ? 'Выберите расписание и файл для загрузки.'
          : 'Ընտրեք ժամանակացույցը և ֆայլը:')

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
            <IconButton
              size="small"
              sx={{ color: greenTheme.primary, fontSize: 18 }}
              onClick={handleBack}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {isAddMode
                ? (langIndex === 1 ? 'Upload phone numbers from Excel' : langIndex === 2 ? 'Загрузить номера из Excel' : 'Վերբեռնել հեռախոսահամարներ Excel-ից')
                : (langIndex === 1 ? 'Upload file' : langIndex === 2 ? 'Загрузить файл' : 'Վերբեռնել ֆայլ')}{' '}
              — {menu.autoCall[langIndex]}
            </Typography>
          </Box>

          <Alert icon={<InfoIcon />} severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {infoText}
            </Typography>
          </Alert>

          <Paper sx={{ p: 3, border: `1px solid ${greenTheme.primary}` }}>
            <Typography variant="h6" sx={sectionTitleStyles}>
              {langIndex === 1 ? 'Schedule' : langIndex === 2 ? 'Расписание' : 'Ժամանակացույց'}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
            {isAddMode && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={inputLabelStyles}>{auto.type[langIndex]}</InputLabel>
                  <Select
                    value={form.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    label={auto.type[langIndex]}
                    sx={selectStyles}
                    onOpen={() => form.type === 'group' && (!files || !files.length) && dispatch(fileRequest(1))}
                  >
                    <MenuItem value="auto">Auto call</MenuItem>
                    <MenuItem value="group">Group call</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={isAddMode ? 6 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={inputLabelStyles}>{auto.num[langIndex]}</InputLabel>
                <Select
                  value={form.num}
                  onChange={(e) => handleChange('num', e.target.value)}
                  label={auto.num[langIndex]}
                  sx={selectStyles}
                >
                  <MenuItem value="">{input.select[langIndex]}</MenuItem>
                  {!isAddMode
                    ? numOptionsEdit.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))
                    : form.type === 'group'
                      ? numOptionsGroup.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))
                      : numOptionsAuto.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={inputLabelStyles}>{short.week[langIndex]} Start</InputLabel>
                <Select
                  value={form.weekStart}
                  onChange={(e) => handleChange('weekStart', e.target.value)}
                  label={`${short.week[langIndex]} Start`}
                  sx={selectStyles}
                >
                  {weekDays.map((d, i) => (
                    <MenuItem key={i} value={d[langIndex]}>{d[langIndex]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={inputLabelStyles}>{short.week[langIndex]} End</InputLabel>
                <Select
                  value={form.weekEnd}
                  onChange={(e) => handleChange('weekEnd', e.target.value)}
                  label={`${short.week[langIndex]} End`}
                  sx={selectStyles}
                >
                  {weekDays.map((d, i) => (
                    <MenuItem key={i} value={d[langIndex]}>{d[langIndex]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label={`${short.time[langIndex]} Start`}
                value={form.timeStart}
                onChange={(e) => handleTimeInput('timeStart', e.target.value)}
                onBlur={() => handleTimeBlur('timeStart')}
                placeholder="HH:mm"
                fullWidth
                sx={inputStyles}
                InputLabelProps={{ sx: inputLabelStyles }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label={`${short.time[langIndex]} End`}
                value={form.timeEnd}
                onChange={(e) => handleTimeInput('timeEnd', e.target.value)}
                onBlur={() => handleTimeBlur('timeEnd')}
                placeholder="HH:mm"
                fullWidth
                sx={inputStyles}
                InputLabelProps={{ sx: inputLabelStyles }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  size="small"
                  label={`${auto.ok[langIndex]} Start`}
                  type="number"
                  value={form.okStart}
                  onChange={(e) => handleChange('okStart', e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
                  sx={{ flex: 1, ...inputStyles }}
                  InputLabelProps={{ sx: inputLabelStyles }}
                />
                <Typography sx={{ color: 'text.secondary' }}>-</Typography>
                <TextField
                  size="small"
                  label={`${auto.ok[langIndex]} End`}
                  type="number"
                  value={form.okEnd}
                  onChange={(e) => handleChange('okEnd', e.target.value === '' ? 60 : parseInt(e.target.value, 10) || 60)}
                  sx={{ flex: 1, ...inputStyles }}
                  InputLabelProps={{ sx: inputLabelStyles }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                label={auto.attempt[langIndex]}
                type="number"
                value={form.attempt}
                onChange={(e) => handleChange('attempt', e.target.value === '' ? 2 : parseInt(e.target.value, 10) || 2)}
                fullWidth
                sx={inputStyles}
                InputLabelProps={{ sx: inputLabelStyles }}
              />
            </Grid>
          </Grid>

            <Divider sx={{ my: 3 }} />

            {isAddMode ? (
              <>
                <Typography variant="h6" sx={sectionTitleStyles}>
                  {langIndex === 1 ? 'Phone numbers Excel (one number per row)' : langIndex === 2 ? 'Excel с номерами (один номер в строке)' : 'Հեռախոսահամարների Excel'}
                </Typography>
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {langIndex === 1 ? 'Use a column named "Phone" or "Number". Download template:' : langIndex === 2 ? 'Столбец "Phone" или "Number". Скачать шаблон:' : 'Սյունակ «Phone» կամ «Number»: Ներբեռնել ձևանմուշ:'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTemplate}
                  sx={{
                    textTransform: 'none',
                    borderColor: greenTheme.primary,
                    color: greenTheme.primary,
                    '&:hover': { borderColor: greenTheme.hover, backgroundColor: `${greenTheme.primary}10` },
                  }}
                >
                  {langIndex === 1 ? 'Download template' : langIndex === 2 ? 'Скачать шаблон' : 'Ներբեռնել ձևանմուշ'}
                </Button>
              </Box>

              <Box sx={{ mb: 2 }}>
                <input
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  style={{ display: 'none' }}
                  id="auto-call-excel-upload"
                  type="file"
                  onChange={handleFileChange}
                  disabled={processing}
                />
                <label htmlFor="auto-call-excel-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    disabled={processing}
                    sx={{
                      textTransform: 'none',
                      borderColor: greenTheme.primary,
                      color: greenTheme.primary,
                      px: 3,
                      py: 1.5,
                      '&:hover': { borderColor: greenTheme.hover, backgroundColor: `${greenTheme.primary}10` },
                    }}
                  >
                    {langIndex === 1 ? 'Choose file' : langIndex === 2 ? 'Выбрать файл' : 'Ընտրել ֆայլ'}
                  </Button>
                </label>
                {selectedFile && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{selectedFile.name}</Typography>
                )}
              </Box>

                <Button
                  variant="contained"
                  disabled={!form.num || !selectedFile || processing}
                  onClick={handleUpload}
                  sx={{
                    ...buttonStyles,
                    backgroundColor: greenTheme.primary,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: greenTheme.hover },
                  }}
                >
                  {processing
                    ? (langIndex === 1 ? 'Processing...' : langIndex === 2 ? 'Обработка...' : 'Մշակվում է...')
                    : langIndex === 1
                      ? 'Upload and continue to form'
                      : langIndex === 2
                        ? 'Загрузить и перейти к форме'
                        : 'Վերբեռնել և անցնել ձևին'}
                </Button>
              </>
            ) : (
              <Box sx={{ mb: 2 }}>
                <input
                  style={{ display: 'none' }}
                  id="auto-call-file-upload-edit"
                  type="file"
                  onChange={handleFileUploadEdit}
                  disabled={processing}
                />
                <label htmlFor="auto-call-file-upload-edit">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<UploadFileIcon />}
                    disabled={!form.num || processing}
                    sx={{
                      ...buttonStyles,
                      backgroundColor: greenTheme.primary,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: greenTheme.hover },
                    }}
                  >
                    {processing
                      ? (langIndex === 1 ? 'Uploading...' : langIndex === 2 ? 'Загрузка...' : 'Վերբեռնվում է...')
                      : (langIndex === 1 ? 'Choose file to upload' : langIndex === 2 ? 'Выбрать файл для загрузки' : 'Ընտրել ֆայլ')}
                  </Button>
                </label>
              </Box>
            )}
          </Paper>
        </Paper>
      </div>
    </Wrapper>
  )
}

export default AutoCallExcelUpload
